import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/client';

const MIN_PASSWORD_LENGTH = 8;

/**
 * パスワードの設定（課題 #101）。管理者の「パスワードをリセット」で本人のメールに届く URL を開いて使う（#51）。
 *
 * 以前は、管理者の画面にトークンの文字列が出るだけで、本人が新しいパスワードを決める画面が無かった
 * （BE の POST /v1/auth/set-password はあったが、FE から呼ぶ画面が無かった）。
 * URL は 1 日有効・1 回だけ使える。ログインしていなくても開ける。
 */
export default function SetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('setPassword.tooShort'));
      return;
    }
    if (password !== passwordConfirm) {
      setError(t('setPassword.mismatch'));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await authApi.setPassword({ token, password });
      setDone(true);
      setPassword('');
      setPasswordConfirm('');
    } catch (err) {
      setError(t('setPassword.failed', { message: err instanceof Error ? err.message : t('common.error') }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <h1>{t('setPassword.title')}</h1>
        {!token ? (
          <p className="login-error" role="alert">
            {t('setPassword.noToken')}
          </p>
        ) : done ? (
          <p className="set-password-done" role="status">
            {t('setPassword.done')}
          </p>
        ) : (
          <>
            <p className="login-desc">{t('setPassword.intro')}</p>
            <form onSubmit={handleSubmit} className="login-form" noValidate>
              <label>
                {t('setPassword.password')}
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                />
              </label>
              <label>
                {t('setPassword.passwordConfirm')}
                <input
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>
              {error && (
                <p className="login-error" role="alert">
                  {error}
                </p>
              )}
              <button type="submit" disabled={submitting}>
                {submitting ? t('setPassword.submitting') : t('setPassword.submit')}
              </button>
            </form>
          </>
        )}
        <p className="login-accept-invite-link">
          <Link to="/login">{t('setPassword.toLogin')}</Link>
        </p>
      </div>
    </div>
  );
}
